/**
 * Block 0 Phase 3 (ADR-0013 §3) — UA-based platform detection.
 *
 * Используется только в `features/install/InstallScreen` для выбора между
 * native install prompt (Android Chrome / Edge / Desktop Chrome) и iOS
 * fallback с инструкциями. **НЕ** используется для standalone-detection
 * — для этого `useStandaloneMode` (`matchMedia` + `navigator.standalone`).
 *
 * Почему именно эти эвристики:
 *
 * 1. **Primary UA-match**: `iPad|iPhone|iPod` покрывает iOS Safari ≤ 12
 *    и iPhone/iPod на любой версии. `!window.MSStream` исключает легаси
 *    IE Mobile, который в UA содержит `iPhone` для совместимости —
 *    это known gotcha из MDN BeforeInstallPromptEvent примеров.
 *
 * 2. **iPadOS desktop-mode fallback**: с iPadOS 13+ Safari по умолчанию
 *    репортит UA как Macintosh + multitouch. Без этой ветки iPad-юзер
 *    провалится в Android prompt branch и будет жать кнопку, на которую
 *    iOS не реагирует. Сужаем до `Macintosh` UA (не любой touch-девайс),
 *    плюс требуем `maxTouchPoints > 1` — Mac Mini / iMac имеют
 *    `maxTouchPoints === 0`.
 *
 * 3. **`isAndroid`** добавлен для симметрии и будущего использования —
 *    Step 3 InstallScreen его не вызывает (Android-ветка определяется
 *    наличием `beforeinstallprompt`, не UA). На случай, если в Phase 4+
 *    понадобится Android-specific UI (например, Samsung Internet share
 *    sheet), отдельный модуль уже готов.
 */

type WindowWithMSStream = Window & { MSStream?: unknown };

export function isIOS(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  const w = window as WindowWithMSStream;

  if (/iPad|iPhone|iPod/.test(ua) && w.MSStream === undefined) {
    return true;
  }
  if (
    /Macintosh/.test(ua) &&
    'ontouchstart' in window &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /Android/.test(navigator.userAgent);
}
